/**
 * Transcript panel styles - layout and interactive states.
 * Colors come from MUI theme.
 */

import { SxProps, Theme } from '@mui/material';

export const transcriptPanelStyles: Record<string, SxProps<Theme>> = {
  emptyContainer: {
    height: '100%',
    minHeight: '150px',
    gap: 2,
    p: 2,
    bgcolor: 'custom.backgroundAlt',
  },

  scrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    width: '100%',
    height: '100%',
    minHeight: 0,
    boxSizing: 'border-box',
    p: 1,
    pt: 0.5,
    bgcolor: 'custom.backgroundAlt',
  },

  segmentWrapper: {
    px: 1,
    py: 0.25,
    width: '100%',
    boxSizing: 'border-box',
  },

  chipOverride: {
    alignSelf: 'flex-start',
  },

  scrollToLatestButton: {
    position: 'sticky',
    bottom: 16,
    alignSelf: 'center',
    width: 'fit-content',
    maxWidth: 'calc(100% - 16px)',
    bgcolor: 'primary.main',
    color: 'white',
    px: 1.25,
    py: 0.5,
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    boxShadow: 'custom.cardShadow',
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: 'primary.dark',
      transform: 'translateY(-2px)',
      boxShadow: 'custom.dropdownShadow',
    },
    zIndex: 10,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    whiteSpace: 'nowrap',
  },

  processingContainer: {
    gap: 1,
    py: 1.5,
    px: 2,
    color: 'text.secondary',
    justifyContent: 'center',
    mt: 1,
    width: '100%',
    boxSizing: 'border-box',
  },

  centeredText: {
    textAlign: 'center',
    color: 'text.disabled',
  },

  emptyTitle: {
    mb: 1,
    textAlign: 'center',
    color: 'text.disabled',
  },

  editButton: {
    textTransform: 'none',
    fontSize: '13px',
    fontWeight: 500,
    minWidth: 'auto',
    px: 1.5,
    py: 0.5,
  },

  editedChip: {
    borderLeft: '3px solid',
    borderColor: 'info.main',
  },

  inlineEditContainer: {
    display: 'flex',
    flexDirection: 'column',
    px: 1,
    py: 0.5,
  },

  saveErrorText: {
    fontSize: '12px',
    color: 'error.main',
    px: 1,
    pt: 0.5,
  },

  characterCount: {
    fontSize: '12px',
    color: 'text.secondary',
    textAlign: 'right',
    px: 1,
    pt: 0.5,
  },

  characterCountWarning: {
    fontSize: '12px',
    color: 'error.main',
    textAlign: 'right',
    px: 1,
    pt: 0.5,
    fontWeight: 500,
  },

  editedNote: {
    px: 2,
    py: 0.75,
    fontSize: '12px',
    color: 'text.secondary',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },

  flatTranscriptText: {
    px: 2,
    py: 1,
    lineHeight: 1.6,
    color: 'text.primary',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    userSelect: 'text',
  },

  inlineEditField: {
    '& .MuiInputBase-root': {
      fontSize: '14px',
      lineHeight: 1.6,
      padding: '8px 12px',
      alignItems: 'flex-start',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'divider',
      borderRadius: '8px',
    },
  },
};

export const createChipStyles = (
  scaleFontSize: (size: string) => string
): SxProps<Theme> => ({
  alignSelf: 'flex-start',
  '& .MuiChip-label': {
    fontSize: scaleFontSize('14px'),
  },
});
